import React, { useState, useEffect } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import QuickLogin from '../components/QuickLogin';
import AdminNavigation from '../components/AdminNavigation';
import { motion } from 'framer-motion';
import { 
  Download, 
  Play, 
  RefreshCw, 
  Database, 
  FileText, 
  Users, 
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  Eye
} from 'lucide-react';

const AdminCrawlerPage = () => {
  const { isAuthenticated } = useAdmin();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [crawlerStatus, setCrawlerStatus] = useState(null);
  const [demoFiles, setDemoFiles] = useState([]);
  const [demoSkins, setDemoSkins] = useState(null);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (isAuthenticated) {
      loadCrawlerStatus();
      if (activeTab === 'demos') {
        loadDemoFiles();
      }
    }
  }, [isAuthenticated, activeTab, currentPage]);

  const loadCrawlerStatus = async () => {
    try {
      const response = await adminAPI.getCrawlerStatus();
      if (response.data.success) {
        setCrawlerStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error loading crawler status:', err);
    }
  };

  const loadDemoFiles = async () => {
    try {
      const response = await adminAPI.getDemoFiles(currentPage, 20);
      if (response.data.success) {
        setDemoFiles(response.data.data);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err) {
      console.error('Error loading demo files:', err);
    }
  };

  const loadDemoSkins = async (demoId) => {
    try {
      const response = await adminAPI.getDemoSkins(demoId);
      if (response.data.success) {
        setDemoSkins(response.data.data);
        setSelectedDemo(demoId);
        setActiveTab('skins'); // Automatically switch to skins tab
        toast.success(`Loaded ${response.data.data.skins.length} skins from demo`);
      }
    } catch (err) {
      console.error('Error loading demo skins:', err);
      toast.error('Failed to load demo skins');
    }
  };

  const runCrawlerCommand = async (endpoint, payload = {}, successMessage = 'Command completed successfully') => {
    setIsLoading(true);
    try {
      const response = await adminAPI.runCrawlerCommand(endpoint, payload);
      if (response.data.success) {
        toast.success(successMessage);
        loadCrawlerStatus(); // Refresh status after command
        if (activeTab === 'demos') {
          loadDemoFiles(); // Refresh demo files if on demos tab
        }
      } else {
        toast.error(response.data.message || 'Command failed');
      }
    } catch (err) {
      toast.error('Error running command: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'processing':
        return <Loader className="h-4 w-4 text-blue-400 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'processing':
        return 'text-blue-400';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-yellow-400';
    }
  };

  if (!isAuthenticated) {
    return <QuickLogin />;
  }

  return (
    <>
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 bg-gray-800 rounded-lg shadow-lg min-h-screen"
    >
      <h1 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
        <Database className="h-8 w-8 text-blue-400" />
        <span>Admin Crawler & Demo Processor</span>
      </h1>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-700 p-1 rounded-lg">
        {[
          { id: 'overview', name: 'Overview', icon: Trophy },
          { id: 'crawlers', name: 'Crawlers', icon: Download },
          { id: 'demos', name: 'Demo Processing', icon: FileText },
          { id: 'skins', name: 'Skin Extractions', icon: Users }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-600'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {crawlerStatus && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-300">Demo Files</p>
                      <p className="text-2xl font-bold text-white">{crawlerStatus.demo_stats.total}</p>
                      <p className="text-xs text-gray-400">
                        {crawlerStatus.demo_stats.completed} completed
                      </p>
                    </div>
                    <FileText className="h-8 w-8 text-blue-400" />
                  </div>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-300">Skins Extracted</p>
                      <p className="text-2xl font-bold text-white">{crawlerStatus.total_skins_extracted}</p>
                      <p className="text-xs text-gray-400">From processed demos</p>
                    </div>
                    <Users className="h-8 w-8 text-green-400" />
                  </div>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-300">HLTV Matches</p>
                      <p className="text-2xl font-bold text-white">{crawlerStatus.hltv_matches_total}</p>
                      <p className="text-xs text-gray-400">
                        {crawlerStatus.hltv_matches_completed} completed
                      </p>
                    </div>
                    <Trophy className="h-8 w-8 text-yellow-400" />
                  </div>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-300">Pending Demos</p>
                      <p className="text-2xl font-bold text-white">{crawlerStatus.demo_stats.pending}</p>
                      <p className="text-xs text-gray-400">Ready to process</p>
                    </div>
                    <Clock className="h-8 w-8 text-orange-400" />
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Demo Files</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {crawlerStatus.recent_demos.map((demo) => (
                      <div key={demo.id} className="flex items-center justify-between p-2 bg-gray-600 rounded">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(demo.processing_status)}
                          <div>
                            <p className="text-sm font-medium text-white truncate" title={demo.filename}>
                              {demo.filename.length > 30 ? demo.filename.substring(0, 30) + '...' : demo.filename}
                            </p>
                            <p className="text-xs text-gray-400">
                              {demo.team_a} vs {demo.team_b} • {demo.map_name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">{demo.skins_extracted} skins</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Skin Extractions</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {crawlerStatus.recent_skin_extractions.map((skin) => (
                      <div key={skin.id} className="flex items-center justify-between p-2 bg-gray-600 rounded">
                        <div>
                          <p className="text-sm font-medium text-white">{skin.player_name}</p>
                          <p className="text-xs text-gray-400">
                            {skin.weapon} • {skin.skin_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">{skin.team}</p>
                          <p className="text-xs text-green-400">{skin.confidence_level}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Crawlers Tab */}
      {activeTab === 'crawlers' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-700 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-yellow-400" />
                <span>HLTV Team Rankings</span>
              </h3>
              <p className="text-gray-300 mb-4 text-sm">
                Crawl team rankings, players, and current standings from HLTV.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => runCrawlerCommand('hltv-teams', {}, 'HLTV teams crawled successfully')}
                  disabled={isLoading}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
                >
                  {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span>Crawl Teams & Rankings</span>
                </button>
                <button
                  onClick={() => runCrawlerCommand('hltv-teams', { force: true }, 'HLTV teams force-crawled successfully')}
                  disabled={isLoading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
                >
                  {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span>Force Refresh Teams</span>
                </button>
              </div>
            </div>

            <div className="bg-gray-700 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Trophy className="h-5 w-5 text-blue-400" />
                <span>HLTV Match Results</span>
              </h3>
              <p className="text-gray-300 mb-4 text-sm">
                Crawl recent match results with scores, demos, and tournament data.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => runCrawlerCommand('hltv-results', { limit: 10 }, 'HLTV results crawled successfully')}
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
                >
                  {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span>Crawl 10 Recent Matches</span>
                </button>
                <button
                  onClick={() => runCrawlerCommand('hltv-results', { limit: 20 }, 'HLTV results crawled successfully')}
                  disabled={isLoading}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
                >
                  {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span>Crawl 20 Recent Matches</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Processing Tab */}
      {activeTab === 'demos' && (
        <div className="space-y-6">
          {/* Demo Processing Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-3">Download Demo</h4>
              <p className="text-gray-300 text-sm mb-3">Download demo files from HLTV matches.</p>
              <button
                onClick={() => runCrawlerCommand('demo-download', { limit: 1 }, 'Demo download completed')}
                disabled={isLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
              >
                {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span>Download Demo</span>
              </button>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-3">Scan Demos</h4>
              <p className="text-gray-300 text-sm mb-3">Scan for new demo files in the demos directory.</p>
              <button
                onClick={() => runCrawlerCommand('demo-scan', {}, 'Demo scanning completed')}
                disabled={isLoading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
              >
                {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span>Scan for New Demos</span>
              </button>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-3">Process Demos</h4>
              <p className="text-gray-300 text-sm mb-3">Process pending demos to extract skin data.</p>
              <button
                onClick={() => runCrawlerCommand('demo-process', { limit: 3 }, 'Demo processing completed')}
                disabled={isLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
              >
                {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span>Process 3 Demos</span>
              </button>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-3">Full Process</h4>
              <p className="text-gray-300 text-sm mb-3">Scan and process in one command.</p>
              <button
                onClick={() => runCrawlerCommand('demo-full-process', { limit: 3 }, 'Full demo processing completed')}
                disabled={isLoading}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded flex items-center justify-center space-x-2"
              >
                {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                <span>Scan & Process</span>
              </button>
            </div>
          </div>

          {/* Demo Files Table */}
          <div className="bg-gray-700 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-600">
              <h3 className="text-lg font-semibold text-white">Demo Files</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <thead className="bg-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">File</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Match</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Results</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {demoFiles.map((demo) => (
                    <tr key={demo.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white" title={demo.filename}>
                            {demo.filename.length > 40 ? demo.filename.substring(0, 40) + '...' : demo.filename}
                          </div>
                          <div className="text-sm text-gray-400">{demo.map_name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {demo.team_a} vs {demo.team_b}
                        </div>
                        <div className="text-sm text-gray-400">ID: {demo.hltv_match_id || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(demo.processing_status)}
                          <span className={`text-sm font-medium ${getStatusColor(demo.processing_status)}`}>
                            {demo.processing_status}
                          </span>
                        </div>
                        {demo.duration && (
                          <div className="text-xs text-gray-400">{demo.duration}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{demo.skins_extracted} skins</div>
                        <div className="text-sm text-gray-400">{demo.players_found} players</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {demo.processing_status === 'completed' && demo.skins_extracted > 0 && (
                          <button
                            onClick={() => loadDemoSkins(demo.id)}
                            className="text-blue-400 hover:text-blue-600 mr-2"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-600">
                <div className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Skin Extractions Tab */}
      {activeTab === 'skins' && demoSkins && (
        <div className="space-y-6">
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">
              Skin Extractions from {demoSkins.demo.filename}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{demoSkins.demo.skins_extracted}</div>
                <div className="text-sm text-gray-400">Total Skins</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{demoSkins.demo.players_found}</div>
                <div className="text-sm text-gray-400">Players</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{demoSkins.demo.team_a} vs {demoSkins.demo.team_b}</div>
                <div className="text-sm text-gray-400">Teams</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{demoSkins.demo.map_name}</div>
                <div className="text-sm text-gray-400">Map</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <thead className="bg-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Player</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Weapon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Skin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Wear</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Confidence</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {demoSkins.skins.map((skin) => (
                    <tr key={skin.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{skin.player_name}</div>
                        <div className="text-sm text-gray-400">{skin.team}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{skin.weapon}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">{skin.skin_name}</div>
                        {skin.nametag && (
                          <div className="text-sm text-yellow-400">"{skin.nametag}"</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{skin.wear_name}</div>
                        {skin.wear_float && (
                          <div className="text-sm text-gray-400">{skin.wear_float}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          skin.confidence_level === 'high' ? 'bg-green-100 text-green-800' :
                          skin.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {skin.confidence_level}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
      </div>
    </>
  );
};

export default AdminCrawlerPage;
